import { useEffect, useRef } from 'react';
import * as THREE from 'three';

interface AuroraBackgroundProps {
  /** 0.0 = cool steel blue (raw ore), 1.0 = white-hot forge. Default 0.0 */
  warmth?: number;
}

/**
 * GLSL aurora background with forge color temperature.
 * The warmth uniform blends between cool steel (assessment)
 * and white-hot amber (protocol reveal).
 */
export function AuroraBackground({ warmth = 0 }: AuroraBackgroundProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const warmthRef = useRef(warmth);

  // Keep ref current so the render loop reads the latest value
  warmthRef.current = warmth;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setSize(window.innerWidth, window.innerHeight);
    container.appendChild(renderer.domElement);

    const material = new THREE.ShaderMaterial({
      uniforms: {
        iTime: { value: 0 },
        iResolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
        uWarmth: { value: warmthRef.current },
      },
      vertexShader: `
        void main() {
          gl_Position = vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float iTime;
        uniform vec2 iResolution;
        uniform float uWarmth; // 0 = cool steel, 1 = white-hot

        #define NUM_OCTAVES 3

        float rand(vec2 n) {
          return fract(sin(dot(n, vec2(12.9898, 4.1414))) * 43758.5453);
        }

        float noise(vec2 p) {
          vec2 ip = floor(p);
          vec2 u = fract(p);
          u = u * u * (3.0 - 2.0 * u);
          float res = mix(
            mix(rand(ip), rand(ip + vec2(1.0, 0.0)), u.x),
            mix(rand(ip + vec2(0.0, 1.0)), rand(ip + vec2(1.0, 1.0)), u.x), u.y);
          return res * res;
        }

        float fbm(vec2 x) {
          float v = 0.0;
          float a = 0.3;
          vec2 shift = vec2(100.0);
          mat2 rot = mat2(cos(0.5), sin(0.5), -sin(0.5), cos(0.5));
          for (int i = 0; i < NUM_OCTAVES; ++i) {
            v += a * noise(x);
            x = rot * x * 2.0 + shift;
            a *= 0.4;
          }
          return v;
        }

        void main() {
          vec2 shake = vec2(sin(iTime * 0.8) * 0.003, cos(iTime * 1.2) * 0.003);
          vec2 p = ((gl_FragCoord.xy + shake * iResolution.xy) - iResolution.xy * 0.5)
                   / iResolution.y * mat2(6.0, -4.0, 4.0, 6.0);

          float f = 2.0 + fbm(p + vec2(iTime * 3.0, 0.0)) * 0.5;

          vec4 o = vec4(0.0);
          float w = clamp(uWarmth, 0.0, 1.0);

          for (float i = 0.0; i < 30.0; i++) {
            vec2 v = p + cos(i * i + (iTime + p.x * 0.06) * 0.02 + i * vec2(13.0, 11.0)) * 3.5
                     + vec2(sin(iTime * 2.0 + i) * 0.002, cos(iTime * 2.5 - i) * 0.002);

            float tailNoise = fbm(v + vec2(iTime * 0.3, i)) * 0.3 * (1.0 - (i / 30.0));

            // Cool palette (w=0): steel blue / teal
            vec3 cool = vec3(
              0.06 + 0.12 * sin(i * 0.2 + iTime * 0.25),
              0.12 + 0.20 * cos(i * 0.3 + iTime * 0.3),
              0.25 + 0.18 * sin(i * 0.4 + iTime * 0.2)
            );

            // Hot palette (w=1): amber / gold / white-hot
            vec3 hot = vec3(
              0.35 + 0.20 * sin(i * 0.2 + iTime * 0.3),
              0.18 + 0.15 * cos(i * 0.25 + iTime * 0.25),
              0.06 + 0.08 * sin(i * 0.4 + iTime * 0.2)
            );

            vec3 blended = mix(cool, hot, w);
            vec4 auroraColors = vec4(blended, 1.0);

            vec4 contrib = auroraColors * exp(sin(i * i + iTime * 0.5))
                           / length(max(v, vec2(v.x * f * 0.015, v.y * 1.5)));

            float thinness = smoothstep(0.0, 1.0, i / 30.0) * 0.6;
            o += contrib * (1.0 + tailNoise * 0.6) * thinness;
          }

          o = tanh(pow(o / 100.0, vec4(1.6)));

          // Slightly brighter at high warmth
          float intensity = mix(1.2, 1.6, w);
          gl_FragColor = o * intensity;
        }
      `,
    });

    const geometry = new THREE.PlaneGeometry(2, 2);
    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    let frameId: number;
    const animate = () => {
      material.uniforms.iTime.value += 0.012;
      // Smooth-lerp warmth toward target for silky transitions
      const target = warmthRef.current;
      const current = material.uniforms.uWarmth.value;
      material.uniforms.uWarmth.value += (target - current) * 0.02;
      renderer.render(scene, camera);
      frameId = requestAnimationFrame(animate);
    };
    animate();

    const handleResize = () => {
      renderer.setSize(window.innerWidth, window.innerHeight);
      material.uniforms.iResolution.value.set(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', handleResize);
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      geometry.dispose();
      material.dispose();
      renderer.dispose();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 pointer-events-none z-0"
      style={{ mixBlendMode: 'screen' }}
    />
  );
}
